/**
 * retryAsync — run an async op with a few bounded, linearly-backed-off retries.
 *
 * Born from a real prod incident (2026-07-15): during a deploy the web container's build IO starved the
 * live box and Caddy aborted 61 in-flight GLB responses with "unexpected EOF". The GLB loader had NO retry,
 * so every captain who reached the sea in that ~10 min window fell back to the procedural hull for the WHOLE
 * session ("the little procedural boat"). A couple of cheap retries ride straight over a transient truncation
 * / reset / 5xx; only a genuinely unreachable asset falls through to the procedural fallback.
 *
 * Pure and timer-injectable so it is unit-testable with fake timers (no real sleeping in tests). Linear
 * backoff: after a failed attempt `i` (0-based) it waits `delayMs*(i+1)` before the NEXT try — 400ms, 800ms,
 * … for the defaults. Re-throws the LAST error once `tries` is exhausted, so callers keep their existing
 * try/catch fallback semantics unchanged.
 */
export async function retryAsync<T>(
  op: (attempt: number) => Promise<T>,
  tries = 3,
  delayMs = 400,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<T> {
  const total = Math.max(1, tries | 0);
  let lastErr: unknown;
  for (let i = 0; i < total; i++) {
    try {
      return await op(i);
    } catch (err) {
      lastErr = err;
      if (i < total - 1) await sleep(delayMs * (i + 1));
    }
  }
  throw lastErr;
}

/**
 * Stall guard: no-progress window after which a pending GLB load is treated as DEAD and rejected so the
 * retry can take over. This is an INACTIVITY timeout, deliberately NOT a total deadline: three's FileLoader
 * streams the response body and fires `onProgress` on every chunk, so a legitimately slow download (a 6 MB
 * hero GLB on throttled 4G can take 30s+) keeps resetting the guard and is never aborted-and-restarted from
 * byte 0 — only a connection that delivers ZERO bytes for this long trips it. Prod repro (2026-07-15 follow-
 * up) proved a fetch can stall without ever settling: no error means no retry, and the caller's Promise.all
 * hangs forever. 15s of dead air is conclusive on any mobile network.
 */
export const LOAD_STALL_MS = 15_000;

/**
 * Wrap a three-style `loadAsync(url, onProgress)` so a stalled fetch becomes a REJECTION `retryAsync` can
 * see. Every progress chunk re-arms the timer; resolution/rejection settles once (a late arrival from an
 * abandoned attempt is ignored — the underlying fetch cannot be cancelled from here).
 */
export function loadWithStallGuard<T>(
  loader: { loadAsync(url: string, onProgress?: (e: unknown) => void): Promise<T> },
  url: string,
  stallMs = LOAD_STALL_MS,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const disarm = () => { if (timer !== undefined) { clearTimeout(timer); timer = undefined; } };
    const arm = () => {
      if (settled) return;
      disarm();
      timer = setTimeout(() => {
        if (!settled) { settled = true; reject(new Error(`GLB load stalled (no bytes for ${stallMs} ms): ${url}`)); }
      }, stallMs);
    };
    arm();
    loader.loadAsync(url, arm).then(
      (v) => { if (!settled) { settled = true; disarm(); resolve(v); } },
      (e) => { if (!settled) { settled = true; disarm(); reject(e); } },
    );
  });
}

/**
 * Total-deadline wrapper for promises with NO progress signal (e.g. the dynamic import() of a code-split
 * chunk — small enough that a generous total budget is safe where an inactivity guard is impossible).
 * NOTE for import(): the module map may hand a retry the SAME in-flight promise, so each retry effectively
 * extends the total wait by one deadline — bounded, then the caller's fallback fires.
 */
export function withDeadline<T>(make: () => Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => { if (!settled) { settled = true; reject(new Error(`${label} timed out after ${ms} ms`)); } }, ms);
    make().then(
      (v) => { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } },
      (e) => { if (!settled) { settled = true; clearTimeout(timer); reject(e); } },
    );
  });
}

/**
 * Retry a GLTF-style `loadAsync(url)` a few times before giving up, with each attempt under the stall
 * guard so a hung fetch counts as a failure instead of hanging the caller. On exhaustion it re-throws, so
 * the caller's existing `catch → procedural fallback` still fires — but only after the transient window
 * has had a real chance to pass, instead of on the first aborted byte (or never, on a stall).
 */
export function loadWithRetry<T>(
  loader: { loadAsync(url: string, onProgress?: (e: unknown) => void): Promise<T> },
  url: string,
  tries = 3,
  delayMs = 400,
  stallMs = LOAD_STALL_MS,
): Promise<T> {
  return retryAsync(() => loadWithStallGuard(loader, url, stallMs), tries, delayMs);
}
